import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useOutletContext } from "react-router-dom";
import {
  Download,
  Search,
  Check,
  X,
  CircleDot,
  ChevronRight,
  Mail,
  Building2,
  User,
} from "lucide-react";
import { fetchContacts, patchContact, downloadCsv } from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import { NotesPanel } from "./Applications";

const STATUSES = ["new", "replied", "archived"];
const STATUS_COLORS = {
  new: "bg-ink text-white",
  replied: "bg-emerald-100 text-emerald-900 border border-emerald-300",
  archived: "bg-muted text-ink/60 border border-border",
};

function fmt(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function errStr(d) {
  if (!d) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

export default function Contacts() {
  const [params, setParams] = useSearchParams();
  const { reloadStats } = useOutletContext() || {};
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState(params.get("q") || "");

  const filters = useMemo(
    () => ({
      status: params.get("status") || undefined,
      unread: params.get("unread") === "true" ? true : undefined,
      q: params.get("q") || undefined,
    }),
    [params]
  );

  const load = () => {
    setLoading(true);
    fetchContacts(filters)
      .then(setList)
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === undefined || v === "" || v === null || v === "all") next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
  };

  const onSearch = (e) => {
    e.preventDefault();
    setFilter("q", q.trim() || undefined);
  };

  const openRow = async (row) => {
    setActive(row);
    if (!row.read) {
      try {
        const updated = await patchContact(row.id, { read: true });
        setList((xs) => xs.map((x) => (x.id === row.id ? updated : x)));
        setActive(updated);
        reloadStats?.();
      } catch {/* ignore */}
    }
  };

  const changeStatus = async (row, status) => {
    try {
      const updated = await patchContact(row.id, { status });
      setList((xs) => xs.map((x) => (x.id === row.id ? updated : x)));
      if (active?.id === row.id) setActive(updated);
      reloadStats?.();
      toast.success(`Marked as ${status}.`);
    } catch (err) {
      toast.error(errStr(err?.response?.data?.detail));
    }
  };

  const addNote = async (row, body) => {
    try {
      const updated = await patchContact(row.id, { note: body });
      setList((xs) => xs.map((x) => (x.id === row.id ? updated : x)));
      setActive(updated);
      toast.success("Note added.");
    } catch (err) {
      toast.error(errStr(err?.response?.data?.detail));
    }
  };

  const toggleRead = async (row) => {
    try {
      const updated = await patchContact(row.id, { read: !row.read });
      setList((xs) => xs.map((x) => (x.id === row.id ? updated : x)));
      if (active?.id === row.id) setActive(updated);
      reloadStats?.();
    } catch (err) {
      toast.error(errStr(err?.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-contacts">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="overline text-vermilion mb-3">Inbox</div>
          <h1 className="font-serif text-3xl md:text-4xl tracking-tight">
            Contacts{" "}
            <span className="text-ink/40 font-mono text-sm align-middle">
              {loading ? "…" : `${list.length}`}
            </span>
          </h1>
        </div>
        <button
          onClick={() => downloadCsv("contacts", filters)}
          data-testid="contacts-export"
          className="btn-outline text-[11px]"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-8">
        <form onSubmit={onSearch} className="md:col-span-6 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, company, message…"
            data-testid="contacts-search"
            className="pl-9 h-11 rounded-none border-ink/20"
          />
        </form>
        <div className="md:col-span-3">
          <Select
            value={filters.status ?? "all"}
            onValueChange={(v) => setFilter("status", v === "all" ? undefined : v)}
          >
            <SelectTrigger data-testid="contacts-filter-status" className="rounded-none h-11 border-ink/20 capitalize">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3 flex gap-2">
          <button
            onClick={() => setFilter("unread", filters.unread ? undefined : "true")}
            data-testid="contacts-filter-unread"
            className={`flex-1 h-11 border text-xs uppercase tracking-[0.18em] flex items-center justify-center gap-2 transition-colors ${
              filters.unread
                ? "bg-vermilion text-white border-vermilion"
                : "border-ink/20 text-ink hover:bg-muted"
            }`}
          >
            <CircleDot className="w-3.5 h-3.5" /> Unread
          </button>
          <button
            onClick={() => setParams({})}
            className="h-11 px-4 border border-ink/20 text-xs uppercase tracking-[0.18em] text-ink/70 hover:bg-muted"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="border-t border-border" data-testid="contacts-list">
        <div className="grid grid-cols-12 gap-4 py-3 overline text-ink/40 border-b border-border px-2">
          <div className="col-span-4">From</div>
          <div className="col-span-3">Company · Role</div>
          <div className="col-span-2">Inquiry</div>
          <div className="col-span-2">Received</div>
          <div className="col-span-1 text-right"> </div>
        </div>

        {loading && <div className="py-20 text-center text-ink/50">Loading…</div>}
        {!loading && list.length === 0 && (
          <div className="py-20 text-center text-ink/50">
            No contacts match these filters.
          </div>
        )}

        <ul>
          {list.map((row) => (
            <li
              key={row.id}
              data-testid={`contact-row-${row.id}`}
              className={`grid grid-cols-12 gap-4 py-5 px-2 border-b border-border items-center cursor-pointer hover:bg-muted/60 transition-colors ${
                !row.read ? "bg-vermilion/[0.03]" : ""
              }`}
              onClick={() => openRow(row)}
            >
              <div className="col-span-4 min-w-0 flex items-center gap-3">
                {!row.read && (
                  <span className="w-2 h-2 rounded-full bg-vermilion flex-shrink-0" />
                )}
                <div className="min-w-0">
                  <div className="font-serif text-lg truncate">{row.full_name}</div>
                  <div className="text-xs text-ink/50 truncate">{row.email}</div>
                </div>
              </div>
              <div className="col-span-3 text-sm text-ink/80 truncate">
                {row.company || "—"}
                {row.role ? <span className="text-ink/40"> · {row.role}</span> : null}
              </div>
              <div className="col-span-2">
                <span
                  className={`inline-block px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] font-semibold ${
                    STATUS_COLORS[row.status] || STATUS_COLORS.new
                  }`}
                >
                  {row.status}
                </span>
                <div className="mt-1 text-[10px] uppercase tracking-[0.18em] text-ink/40">
                  {row.inquiry_type}
                </div>
              </div>
              <div className="col-span-2 text-xs text-ink/60">
                {fmt(row.created_at)}
              </div>
              <div className="col-span-1 flex justify-end">
                <ChevronRight className="w-4 h-4 text-ink/40" />
              </div>
            </li>
          ))}
        </ul>
      </div>

      <Sheet
        open={!!active}
        onOpenChange={(v) => {
          if (!v) setActive(null);
        }}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl overflow-y-auto rounded-none border-l border-border p-0"
          data-testid="contact-detail"
        >
          {active && (
            <div>
              <SheetHeader className="p-8 border-b border-border">
                <div className="overline text-vermilion mb-2 capitalize">
                  {active.inquiry_type || "inquiry"}
                </div>
                <SheetTitle className="font-serif text-3xl md:text-4xl leading-[1.05] tracking-tight">
                  {active.full_name}
                </SheetTitle>
                <SheetDescription className="text-sm text-ink/60 mt-1">
                  Received {fmt(active.created_at)}
                </SheetDescription>
              </SheetHeader>

              <div className="p-8 border-b border-border">
                <div className="overline text-ink/60 mb-3">Status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => changeStatus(active, s)}
                      data-testid={`status-btn-${s}`}
                      className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold capitalize transition-colors ${
                        active.status === s
                          ? STATUS_COLORS[s]
                          : "border border-ink/20 text-ink/60 hover:bg-muted"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => toggleRead(active)}
                  data-testid="toggle-read"
                  className="mt-4 text-xs uppercase tracking-[0.18em] text-ink/50 inline-flex items-center gap-2 hover:text-ink"
                >
                  {active.read ? (
                    <>
                      <X className="w-3.5 h-3.5" /> Mark as unread
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5" /> Mark as read
                    </>
                  )}
                </button>
              </div>

              <div className="p-8 border-b border-border space-y-3 text-sm">
                <Row icon={Mail} label="Email" value={<a href={`mailto:${active.email}`} className="link-underline">{active.email}</a>} />
                <Row icon={Building2} label="Company" value={active.company || "—"} />
                <Row icon={User} label="Role" value={active.role || "—"} />
              </div>

              <div className="p-8 border-b border-border">
                <div className="overline text-ink/60 mb-2">Message</div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink">
                  {active.message}
                </p>
              </div>

              <NotesPanel
                key={`notes-${active.id}-${(active.notes || []).length}`}
                notes={active.notes || []}
                onAdd={(body) => addNote(active, body)}
              />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="grid grid-cols-12 gap-3 items-start">
      <div className="col-span-4 flex items-center gap-2 text-ink/50">
        <Icon className="w-3.5 h-3.5" />
        <span className="overline">{label}</span>
      </div>
      <div className="col-span-8 text-ink break-words">{value}</div>
    </div>
  );
}
